import { DestroyRef, inject, Injectable, InjectionToken, type Signal, signal } from '@angular/core';

import { Subject } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { LEAPBOT_MAX_FILE_BYTES } from '../../../shared/utils/leapbot-files.util';
import { redactConsoleArgs } from '../../../shared/utils/redact-log.util';

/** Recording stops by itself after this many seconds, so the video stays under LeapBot's 50 MB. */
export const RECORDING_MAX_SECONDS = 300;

const MAX_CONSOLE_ENTRIES = 500;
const MAX_NETWORK_ENTRIES = 300;
const CONSOLE_LEVELS = ['log', 'info', 'warn', 'error', 'debug'] as const;
const NETWORK_INFO = 'Solo incluye las peticiones HTTP de Fury; no incluye Firestore ni inicio de sesión.';

/** A capture of the user's screen that can be stopped, and tells when the browser ended it. */
export interface LeapBotScreenRecording {
  /** Stops capturing and resolves with the recorded video. */
  stop(): Promise<Blob>;
  /** Registers the callback run when the user ends sharing from the browser's own control. */
  onEnded(handler: () => void): void;
}

/** Asks the browser to capture the screen; rejects with a `DOMException` if the user refuses. */
export type LeapBotScreenRecorderFactory = () => Promise<LeapBotScreenRecording>;

/** Whether this browser can record the screen; replaced in tests. */
export const LEAPBOT_RECORDING_SUPPORTED = new InjectionToken<boolean>('LEAPBOT_RECORDING_SUPPORTED', {
  providedIn: 'root',
  factory: (): boolean =>
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getDisplayMedia === 'function' &&
    typeof MediaRecorder !== 'undefined',
});

/** Starts a real browser screen capture; replaced by a fake in tests. */
export const LEAPBOT_SCREEN_RECORDER = new InjectionToken<LeapBotScreenRecorderFactory>('LEAPBOT_SCREEN_RECORDER', {
  providedIn: 'root',
  factory: (): LeapBotScreenRecorderFactory => async () => {
    const stream = await navigator.mediaDevices.getDisplayMedia({ video: { displaySurface: 'browser' }, audio: false });
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm';
    // A capped bitrate keeps five minutes of video well under 50 MB.
    const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 1_000_000 });
    const chunks: Blob[] = [];
    let endedHandler: () => void = () => undefined;
    recorder.ondataavailable = (event: BlobEvent): void => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    stream.getVideoTracks()[0].onended = (): void => endedHandler();
    recorder.start(1000);
    return {
      onEnded: (handler: () => void): void => {
        endedHandler = handler;
      },
      stop: (): Promise<Blob> =>
        new Promise<Blob>((resolve) => {
          const finish = (): void => {
            stream.getTracks().forEach((track) => track.stop());
            resolve(new Blob(chunks, { type: 'video/webm' }));
          };
          if (recorder.state === 'inactive') {
            finish();
            return;
          }
          recorder.onstop = finish;
          recorder.stop();
        }),
    };
  },
});

/** Outcome of asking to start a recording. */
export type LeapBotRecordingStart = 'started' | 'denied' | 'failed' | 'unsupported';

/** One HTTP request captured while recording; every field is already redacted. */
export interface LeapBotNetworkEntry {
  method: string;
  url: string;
  timestamp: string;
  /** Seconds since the recording started. */
  elapsed: string;
  requestHeaders: Record<string, string>;
  requestBody: string | null;
  /** `0` when there was no response (network failure). */
  status: number;
  durationMs: number;
  responseHeaders: Record<string, string>;
  responseBody: string | null;
  error: string | null;
}

/** The files a finished recording produces. */
export interface LeapBotRecordingResult {
  video: File;
  consoleLogs: File;
  networkLogs: File;
  durationSeconds: number;
  /** True when the recording ended because it reached `RECORDING_MAX_SECONDS`. */
  reachedLimit: boolean;
}

interface ConsoleEntry {
  level: string;
  message: string;
  timestamp: string;
  elapsed: string;
}

type ConsoleMethod = (...args: unknown[]) => void;

/**
 * Records the screen plus what the console and the app's HTTP requests did meanwhile, for the user
 * to attach to a LeapBot message. Nothing leaves the browser from here: the result is three files
 * the user can review and remove before sending. Console arguments and HTTP data are redacted
 * before they are stored, and the original console is restored when the recording ends.
 */
@Injectable({ providedIn: 'root' })
export class LeapBotDiagnosticsService {
  private readonly supported = inject(LEAPBOT_RECORDING_SUPPORTED);
  private readonly createRecording = inject(LEAPBOT_SCREEN_RECORDER);
  private recording: LeapBotScreenRecording | null = null;
  private startedAt = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private consoleEntries: ConsoleEntry[] = [];
  private networkEntries: LeapBotNetworkEntry[] = [];
  private originals: Partial<Record<(typeof CONSOLE_LEVELS)[number], ConsoleMethod>> = {};
  private readonly recordingState = signal(false);
  private readonly elapsedState = signal(0);
  private readonly finishedSubject = new Subject<LeapBotRecordingResult | null>();

  /** Whether a recording is in progress. */
  readonly isRecording: Signal<boolean> = this.recordingState.asReadonly();
  /** Seconds recorded so far. */
  readonly elapsedSeconds: Signal<number> = this.elapsedState.asReadonly();
  /** Whether this browser can record the screen. */
  readonly isSupported: boolean = this.supported;
  /** Emits the result when a recording ends by itself (time limit or browser control), `null` if it failed. */
  readonly finished$ = this.finishedSubject.asObservable();

  constructor() {
    inject(DestroyRef).onDestroy(() => this.cancel());
  }

  /** Whether the URL belongs to LeapBot itself, whose traffic is never part of the log. */
  isLeapBotUrl(url: string): boolean {
    const config = environment.leapbot;
    if (!config) return false;
    try {
      return new URL(url, window.location.origin).origin === new URL(config.apiUrl).origin;
    } catch {
      return false;
    }
  }

  /** Adds an HTTP request to the log; ignored when nothing is being recorded. */
  recordRequest(entry: Omit<LeapBotNetworkEntry, 'elapsed' | 'timestamp'>): void {
    if (!this.recordingState()) return;
    this.networkEntries.push({ ...entry, timestamp: new Date().toISOString(), elapsed: this.elapsedLabel() });
    if (this.networkEntries.length > MAX_NETWORK_ENTRIES) this.networkEntries.shift();
  }

  /**
   * Starts recording after the browser asks the user what to share. Refusing the prompt is not an
   * error (`'denied'`); any other failure is `'failed'`.
   */
  async start(): Promise<LeapBotRecordingStart> {
    if (!this.supported) return 'unsupported';
    if (this.recordingState()) return 'started';
    try {
      this.recording = await this.createRecording();
    } catch (error) {
      const name = error instanceof DOMException ? error.name : '';
      return name === 'NotAllowedError' || name === 'AbortError' || name === 'SecurityError' ? 'denied' : 'failed';
    }
    this.consoleEntries = [];
    this.networkEntries = [];
    this.startedAt = Date.now();
    this.elapsedState.set(0);
    this.recordingState.set(true);
    this.hookConsole();
    this.recording.onEnded(() => void this.finishByItself(false));
    this.timer = setInterval(() => {
      this.elapsedState.update((seconds) => seconds + 1);
      if (this.elapsedState() >= RECORDING_MAX_SECONDS) void this.finishByItself(true);
    }, 1000);
    return 'started';
  }

  /**
   * Stops recording and builds the files.
   *
   * @returns The files, or `null` when nothing was recording or the video is empty or over 50 MB.
   */
  async stop(): Promise<LeapBotRecordingResult | null> {
    return this.finish(false);
  }

  /** Stops recording and throws everything away; used when the chat closes or the user signs out. */
  cancel(): void {
    const recording = this.recording;
    if (!recording) return;
    this.release();
    void recording.stop();
  }

  private async finishByItself(reachedLimit: boolean): Promise<void> {
    this.finishedSubject.next(await this.finish(reachedLimit));
  }

  private async finish(reachedLimit: boolean): Promise<LeapBotRecordingResult | null> {
    const recording = this.recording;
    if (!recording) return null;
    const durationSeconds = Math.round((Date.now() - this.startedAt) / 1000);
    const consoleEntries = this.consoleEntries;
    const networkEntries = this.networkEntries;
    this.release();
    const blob = await recording.stop();
    if (blob.size === 0 || blob.size > LEAPBOT_MAX_FILE_BYTES) return null;

    const stamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const meta = {
      recordedAt: new Date().toISOString(),
      duration: `${durationSeconds}s`,
      route: window.location.pathname,
      userAgent: navigator.userAgent,
    };
    const json = (name: string, info: string, entries: unknown[]): File =>
      new File([JSON.stringify({ _info: info, ...meta, entries }, null, 2)], name, { type: 'application/json' });
    return {
      video: new File([blob], `recording-${stamp}.webm`, { type: 'video/webm' }),
      consoleLogs: json('console-logs.json', 'Mensajes de la consola de Fury durante la grabación.', consoleEntries),
      networkLogs: json('network-logs.json', NETWORK_INFO, networkEntries),
      durationSeconds,
      reachedLimit,
    };
  }

  private release(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.unhookConsole();
    this.recording = null;
    this.recordingState.set(false);
  }

  private elapsedLabel(): string {
    return `${((Date.now() - this.startedAt) / 1000).toFixed(2)}s`;
  }

  private hookConsole(): void {
    for (const level of CONSOLE_LEVELS) {
      const original: ConsoleMethod = console[level];
      this.originals[level] = original;
      console[level] = (...args: unknown[]): void => {
        try {
          this.consoleEntries.push({
            level,
            message: redactConsoleArgs(args),
            timestamp: new Date().toISOString(),
            elapsed: this.elapsedLabel(),
          });
          if (this.consoleEntries.length > MAX_CONSOLE_ENTRIES) this.consoleEntries.shift();
        } catch {
          // Capturing must never break the app.
        }
        original.apply(console, args);
      };
    }
  }

  private unhookConsole(): void {
    for (const level of CONSOLE_LEVELS) {
      const original = this.originals[level];
      if (original) console[level] = original;
    }
    this.originals = {};
  }
}
